import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import FileSaver from "file-saver";
import { baseUrl } from "@/consts";
import { ProductType, Supplier } from "@/interfaces";
import {
  formatAxiosError,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
  isNumber,
} from "@/utils";
import { colors, sxButton } from "@/theme";

export type PrintProductListDialogProps = {
  open?: boolean;
  onClose?: () => void;
  /** Creator (createdBy) from the accordion that opened the dialog. */
  createdBy?: string;
  /** All suppliers from GET /Supplier. */
  suppliers?: Supplier[];
  /** All product types (from producttype endpoint). */
  productTypes?: ProductType[];
};

const PrintProductListDialog: React.FC<PrintProductListDialogProps> = ({
  open = false,
  onClose,
  createdBy,
  suppliers,
  productTypes,
}) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>("");
  const [selectedProductTypeId, setSelectedProductTypeId] = useState<string>("");

  const supplierList = suppliers ?? [];
  const selectedSupplier =
    isNonEmptyString(selectedSupplierId) && isNonEmptyArray(supplierList)
      ? supplierList.find(
          (s) => String(s.supplierId) === selectedSupplierId
        )
      : undefined;

  useEffect(() => {
    if (!open) {
      setSelectedSupplierId("");
      setSelectedProductTypeId("");
    }
  }, [open]);

  const productTypeList = productTypes ?? [];

  const handlePrint = async () => {
    if (!selectedSupplier || !isNonEmptyString(createdBy)) return;

    const params = new URLSearchParams();
    if (isNumber(selectedSupplier.supplierId)) {
      params.append(
        "supplierId",
        (selectedSupplier.supplierId as number).toString()
      );
    }
    params.append("createdBy", createdBy as string);
    if (isNonEmptyString(selectedProductTypeId)) {
      params.append("productTypeId", selectedProductTypeId);
    }

    const url = `${baseUrl.DOCUMENT}/getproductlist?${params.toString()}`;
    try {
      const { data } = await axios.get<Blob>(url, { responseType: "blob" });
      FileSaver.saveAs(data, "product-list.pdf");
      onClose?.();
    } catch (e) {
      alert(formatAxiosError(e));
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Print barcode PDF</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel id="print-supplier-label">Supplier</InputLabel>
          <Select
            labelId="print-supplier-label"
            id="print-supplier"
            value={selectedSupplierId}
            label="Supplier"
            onChange={({ target: { value } }) =>
              setSelectedSupplierId(value as string)
            }
          >
            <MenuItem value="">
              <em>Select supplier</em>
            </MenuItem>
            {isNonEmptyArray(supplierList) &&
              supplierList.map((s) => (
                <MenuItem
                  key={s.supplierId}
                  value={s.supplierId != null ? String(s.supplierId) : ""}
                >
                  {s.supplierName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel id="print-product-type-label">Product type</InputLabel>
          <Select
            labelId="print-product-type-label"
            id="print-product-type"
            value={selectedProductTypeId}
            label="Product type"
            onChange={({ target: { value } }) =>
              setSelectedProductTypeId(value as string)
            }
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {isNonEmptyArray(productTypeList) &&
              productTypeList.map((pt) => (
                <MenuItem
                  key={pt.productTypeId}
                  value={pt.productTypeId != null ? String(pt.productTypeId) : ""}
                >
                  {pt.productTypeName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: colors.neutralGrey700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={!isNonEmptyObject(selectedSupplier)}
          sx={sxButton}
        >
          Print PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintProductListDialog;
